import { normalizePhone } from 'cpool-schemas';
import type { ValueOf } from 'cpool-utils';

import {
    type OutboundStartChatPayload,
    resolveOutboundStartChatPayload,
} from '~/features/whatsapp/utils/resolveOutboundStartChatPayload';

export const START_CHAT_MISSING_REASONS = {
    COAPPLICANT_HIDDEN: 'coApplicantHidden',
    DATA_PROTECTION: 'dataProtection',
    NO_APPLICANT_PHONE: 'noApplicantPhone',
    NO_PHONE_NUMBER: 'noPhoneNumber',
    NOT_PERMITTED: 'notPermitted',
} as const;

export type StartChatMissingReason = ValueOf<typeof START_CHAT_MISSING_REASONS>;

export type ResolveApplicantStartChatResult =
    | { missingReason: null; payload: OutboundStartChatPayload }
    | { missingReason: StartChatMissingReason | null; payload: null };

/**
 * Pure eligibility check for starting a chat with a given applicant slot. Mirrors backend's
 * `isCoApplicantOutboundStartBlocked` — only KN2 needs the data-protection agreement.
 */
export const resolveApplicantStartChat = ({
    applicantPhone,
    applicantSlot,
    canStart,
    coApplicantHidden,
    dataProtectionAgreed,
    divisionId,
    loanRequestId,
    phoneNumberId,
}: {
    applicantPhone: string | null | undefined;
    applicantSlot: 1 | 2;
    canStart: boolean;
    coApplicantHidden?: boolean | null;
    dataProtectionAgreed?: boolean | null;
    divisionId: string | null | undefined;
    loanRequestId: string;
    phoneNumberId: string | null | undefined;
}): ResolveApplicantStartChatResult => {
    // Only the `adviser` role can startChat — TL/DL sees the not-permitted reason.
    if (!canStart) {
        return { missingReason: START_CHAT_MISSING_REASONS.NOT_PERMITTED, payload: null };
    }

    if (applicantSlot === 2 && coApplicantHidden === true) {
        return { missingReason: START_CHAT_MISSING_REASONS.COAPPLICANT_HIDDEN, payload: null };
    }

    if (applicantSlot === 2 && dataProtectionAgreed !== true) {
        return { missingReason: START_CHAT_MISSING_REASONS.DATA_PROTECTION, payload: null };
    }

    if (!applicantPhone || !normalizePhone(applicantPhone)) {
        return { missingReason: START_CHAT_MISSING_REASONS.NO_APPLICANT_PHONE, payload: null };
    }

    if (!divisionId || !phoneNumberId) {
        return { missingReason: START_CHAT_MISSING_REASONS.NO_PHONE_NUMBER, payload: null };
    }

    const payload = resolveOutboundStartChatPayload({ applicantPhone, divisionId, loanRequestId, phoneNumberId });
    if (!payload) {
        return { missingReason: START_CHAT_MISSING_REASONS.NO_APPLICANT_PHONE, payload: null };
    }

    return { missingReason: null, payload };
};
